import { useState, useRef, useEffect } from 'react';
import apiClient from '../api/client';
import { CreateProductDto, CompanyDto, ProductDetailDto, UpdateProductDto } from '../types/dtos';
import toast from 'react-hot-toast';

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  product?: ProductDetailDto | null; // ✅ Supports Edit Mode
}

export default function AddProductModal({ isOpen, onClose, onSuccess, product }: AddProductModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const isEdit = !!product;

  const [formData, setFormData] = useState<CreateProductDto>({
    // ... existing initial state ...
    productId: '',
    category: 'Insurance',
    name: '',
    description: '',
    imageUrl: '',
    isActive: true,
    monthlyPremium: 0,
    yearlyPremium: 0,
    payout: 0,
    hasTiers: false,
    tiers: [],
    isRecommended: false,
    companyId: '',
    features: '[]',
    benefits: '[]',
    faqs: '[]',
    documents: '[]',
    requiresApproval: false,
    onboardingFlow: 'Default',
    premiumDueDayOfMonth: undefined,
    policyDurationMonths: undefined,
    premiumFrequency: 'Monthly',
    onceOffPremium: 0,
    questionsJson: '[]',
    declarationText: '',
    allowsBeneficiaries: false,
    maxBeneficiaries: 0,
    beneficiaryPremiumIncrement: 0,
    isDiscontinued: false,
    eligibilityCriteriaJson: '[]',
    claimConfigurationJson: '{"requiredDocuments":[], "requiredFields":[]}',
    onboardingConfigurationJson: '{"requiredDocuments":[], "requiredFields":[]}',
  });

  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValidationErrors({});
      setImageFile(null); // Reset file selection on open
      if (product) {
        setShowDraftBanner(false);
        // ... existing edit pre-fill ...
        setFormData({
          productId: product.productId,
          category: product.category,
          name: product.name,
          description: product.description,
          imageUrl: product.imageUrl,
          isActive: product.isActive,
          monthlyPremium: product.monthlyPremium || 0,
          yearlyPremium: product.yearlyPremium || 0,
          payout: product.payout || 0,
          hasTiers: product.hasTiers || false,
          tiers: product.tiers || [],
          isRecommended: product.isRecommended || false,
          companyId: product.companyId || '',
          features: product.features || '[]',
          benefits: product.benefits || '[]',
          faqs: product.faqs || '[]',
          documents: product.documents || '[]',
          requiresApproval: product.requiresApproval || false,
          onboardingFlow: product.onboardingFlow || 'Default',
          premiumDueDayOfMonth: product.premiumDueDayOfMonth,
          policyDurationMonths: product.policyDurationMonths,
          premiumFrequency: product.premiumFrequency || 'Monthly',
          onceOffPremium: product.onceOffPremium || 0,
          questionsJson: product.questionsJson || '[]',
          declarationText: product.declarationText || '',
          allowsBeneficiaries: product.allowsBeneficiaries || false,
          maxBeneficiaries: product.maxBeneficiaries || 0,
          beneficiaryPremiumIncrement: product.beneficiaryPremiumIncrement || 0,
          isDiscontinued: product.isDiscontinued || false,
          eligibilityCriteriaJson: product.eligibilityCriteriaJson || '[]',
          claimConfigurationJson: product.claimConfigurationJson || '{"requiredDocuments":[], "requiredFields":[]}',
          onboardingConfigurationJson: product.onboardingConfigurationJson || '{"requiredDocuments":[], "requiredFields":[]}',
        });
      } else {
        const saved = localStorage.getItem('xtrem_admin_product_draft');
        if (saved) {
          setShowDraftBanner(true);
        } else {
          setShowDraftBanner(false);
        }
        // ... existing create reset ...
        setFormData({
          productId: '',
          category: 'Insurance',
          name: '',
          description: '',
          imageUrl: '',
          isActive: true,
          monthlyPremium: 0,
          yearlyPremium: 0,
          payout: 0,
          hasTiers: false,
          tiers: [],
          isRecommended: false,
          companyId: '',
          features: '[]',
          benefits: '[]',
          faqs: '[]',
          documents: '[]',
          requiresApproval: false,
          onboardingFlow: 'Default',
          premiumDueDayOfMonth: undefined,
          policyDurationMonths: undefined,
          premiumFrequency: 'Monthly',
          onceOffPremium: 0,
          allowsBeneficiaries: false,
          maxBeneficiaries: 0,
          beneficiaryPremiumIncrement: 0,
          isDiscontinued: false,
          eligibilityCriteriaJson: '[]',
          claimConfigurationJson: '{"requiredDocuments":[], "requiredFields":[]}',
          onboardingConfigurationJson: '{"requiredDocuments":[], "requiredFields":[]}',
        });
      }
      // ... existing initModal ...
      const initModal = async () => {
        try {
          const profileRes = await apiClient.get<any>('/admin/profile');
          const isSuper = profileRes.data.company === 'Xtrem Group';
          setIsSuperAdmin(isSuper);

          if (isSuper) {
            const companiesRes = await apiClient.get<CompanyDto[]>('/admin/companies');
            setCompanies(companiesRes.data);
          }
        } catch (err) {
          console.error('[AddProductModal] Init failed:', err);
        }
      };
      initModal();
    }
  }, [isOpen, product]);

  // Save product form draft
  useEffect(() => {
    if (!isOpen || isEdit) return;
    // Don't save blank/empty drafts
    if (!formData.productId && !formData.name && !formData.description) return;
    localStorage.setItem('xtrem_admin_product_draft', JSON.stringify(formData));
  }, [formData, isOpen, isEdit]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let finalValue: any = value;
    if (type === 'number') {
      finalValue = value === '' ? 0 : parseFloat(value);
    }

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError("File size too large. Please select an image under 2MB.");
        return;
      }
      setImageFile(file);
      setFormData(prev => ({ ...prev, imageUrl: URL.createObjectURL(file) }));
      setError(null);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setValidationErrors({});

    const errors: Record<string, string> = {};

    // 1. Product ID Code
    if (!formData.productId || !formData.productId.trim()) {
      errors.productId = "Product ID is required.";
    } else if (formData.productId.length > 50) {
      errors.productId = "Product ID cannot exceed 50 characters.";
    }

    // 2. Name
    if (!formData.name || !formData.name.trim()) {
      errors.name = "Product Name is required.";
    } else if (formData.name.length > 100) {
      errors.name = "Product Name cannot exceed 100 characters.";
    }

    // 3. Category
    if (!formData.category || !formData.category.trim()) {
      errors.category = "Category is required.";
    } else if (formData.category.length > 50) {
      errors.category = "Category cannot exceed 50 characters.";
    }

    // 4. Description
    if (formData.description && formData.description.length > 250) {
      errors.description = "Description cannot exceed 250 characters.";
    }

    // 5. Premiums & Payout (If not tier-based)
    if (!formData.hasTiers) {
      const mPremium = formData.monthlyPremium || 0;
      const yPremium = formData.yearlyPremium || 0;
      const oPremium = formData.onceOffPremium || 0;
      if (mPremium <= 0 && yPremium <= 0 && oPremium <= 0) {
        errors.premiums = "At least one premium option (Monthly, Yearly, or Once-off) must be greater than 0.";
      }
      if (mPremium < 0) errors.monthlyPremium = "Monthly Premium must be positive.";
      if (yPremium < 0) errors.yearlyPremium = "Yearly Premium must be positive.";
      if (oPremium < 0) errors.onceOffPremium = "Once-off Premium must be positive.";
      if ((formData.payout || 0) < 0) {
        errors.payout = "Payout must be a positive value.";
      }
    }

    // 6. Beneficiary limits
    if (formData.allowsBeneficiaries) {
      if (!formData.maxBeneficiaries || formData.maxBeneficiaries <= 0) {
        errors.maxBeneficiaries = "Max Beneficiaries must be greater than 0 when beneficiaries are allowed.";
      }
      if ((formData.beneficiaryPremiumIncrement || 0) < 0) {
        errors.beneficiaryPremiumIncrement = "Premium increment must be positive.";
      }
    }

    // 7. Premium Due Date and Policy Expiry limits
    if (formData.premiumDueDayOfMonth !== undefined && (formData.premiumDueDayOfMonth < 1 || formData.premiumDueDayOfMonth > 31)) {
      errors.premiumDueDayOfMonth = "Premium Due Day must be between 1 and 31.";
    }
    if (formData.policyDurationMonths !== undefined && formData.policyDurationMonths <= 0) {
      errors.policyDurationMonths = "Policy Duration must be a positive number.";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError("Please fix the validation errors below.");
      setLoading(false);
      return;
    }

    try {
      const data = new FormData();

      // Basic Fields
      data.append('ProductId', formData.productId);
      data.append('Category', formData.category);
      data.append('Name', formData.name);
      data.append('Description', formData.description || '');
      data.append('IsActive', (formData.isActive ?? true).toString());
      data.append('MonthlyPremium', (formData.monthlyPremium || 0).toString());
      data.append('YearlyPremium', (formData.yearlyPremium || 0).toString());
      data.append('Payout', (formData.payout || 0).toString());
      data.append('OnceOffPremium', (formData.onceOffPremium || 0).toString());
      data.append('HasTiers', formData.hasTiers.toString());
      data.append('IsRecommended', formData.isRecommended.toString());
      data.append('CompanyId', formData.companyId || '');
      data.append('RequiresApproval', formData.requiresApproval.toString());
      data.append('OnboardingFlow', formData.onboardingFlow || 'Default');
      data.append('OnboardingConfigurationJson', formData.onboardingConfigurationJson || '{"requiredDocuments":[], "requiredFields":[]}');
      data.append('AllowsBeneficiaries', formData.allowsBeneficiaries.toString());
      data.append('MaxBeneficiaries', (formData.maxBeneficiaries || 0).toString());
      data.append('BeneficiaryPremiumIncrement', (formData.beneficiaryPremiumIncrement || 0).toString());
      data.append('IsDiscontinued', (formData.isDiscontinued || false).toString());
      data.append('EligibilityCriteriaJson', formData.eligibilityCriteriaJson || '[]');
      data.append('ClaimConfigurationJson', formData.claimConfigurationJson || '{"requiredDocuments":[], "requiredFields":[]}');

      if (formData.premiumDueDayOfMonth) data.append('PremiumDueDayOfMonth', formData.premiumDueDayOfMonth.toString());
      if (formData.policyDurationMonths) data.append('PolicyDurationMonths', formData.policyDurationMonths.toString());
      if (formData.premiumFrequency) data.append('PremiumFrequency', formData.premiumFrequency);

      // Stringified JSON Fields (Backend expects string for these based on DTO)
      data.append('Features', formData.features || '[]');
      data.append('Benefits', formData.benefits || '[]');
      data.append('Faqs', formData.faqs || '[]');
      data.append('Documents', formData.documents || '[]');
      data.append('QuestionsJson', formData.questionsJson || '[]');
      data.append('DeclarationText', formData.declarationText || '');

      // Tiers (Complex Object List)
      if (formData.hasTiers && formData.tiers) {
        formData.tiers.forEach((tier, index) => {
          if (tier.id) {
            data.append(`Tiers[${index}].Id`, tier.id);
          }
          data.append(`Tiers[${index}].Name`, tier.name);
          data.append(`Tiers[${index}].MonthlyPremium`, (tier.monthlyPremium || 0).toString());
          data.append(`Tiers[${index}].YearlyPremium`, (tier.yearlyPremium || 0).toString());
          data.append(`Tiers[${index}].Payout`, (tier.payout || 0).toString());
          data.append(`Tiers[${index}].OnceOffPremium`, (tier.onceOffPremium || 0).toString());
          data.append(`Tiers[${index}].Description`, tier.description || '');
          if (tier.policyDurationMonths) {
            data.append(`Tiers[${index}].PolicyDurationMonths`, tier.policyDurationMonths.toString());
          }
        });
      }

      // File
      if (imageFile) {
        data.append('ImageFile', imageFile);
      }

      const headers = { 'Content-Type': 'multipart/form-data' };

      if (isEdit && product) {
        await apiClient.put(`/admin/products/${product.productId}`, data, { headers });
      } else {
        await apiClient.post('/admin/products', data, { headers });
      }
      localStorage.removeItem('xtrem_admin_product_draft');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Failed to save product:', err);
      setError(err.response?.data?.message || 'Failed to save product. Please check your inputs.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!product || !window.confirm(`Are you sure you want to delete "${product.name}"? This action cannot be undone if no customers are assigned.`)) return;

    setLoading(true);
    setError(null);
    try {
      await apiClient.delete(`/admin/products/${product.productId}`);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Failed to delete product:', err);
      setError(err.response?.data?.message || 'Failed to delete product. It might be in use by customers.');
    } finally {
      setLoading(false);
    }
  };

  // --- Helpers for List Management ---
  const handleListUpdate = (key: keyof CreateProductDto, action: 'add' | 'remove' | 'update', index?: number, value?: any) => {
    const currentList = JSON.parse((formData[key] as string) || '[]');
    let newList = [...currentList];

    if (action === 'add') {
      if (key === 'faqs') newList.push({ question: '', answer: '' });
      else if (key === 'documents') newList.push({ name: '', url: '' });
      else newList.push('');
    } else if (action === 'remove' && index !== undefined) {
      newList.splice(index, 1);
    } else if (action === 'update' && index !== undefined) {
      newList[index] = value;
    }

    setFormData(prev => ({ ...prev, [key]: JSON.stringify(newList) }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto font-sans">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-xl">
          <h2 className="text-xl font-bold text-gray-800">
            {isEdit ? `Edit Product: ${product?.name}` : 'Add New Product Policy'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700 text-sm animate-pulse">
              {error}
            </div>
          )}

          {showDraftBanner && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-amber-900 animate-in fade-in slide-in-from-top-4 duration-200">
              <div className="flex items-start gap-2.5">
                <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-amber-800">Unsaved Draft Detected</h4>
                  <p className="text-xs text-amber-700 mt-0.5">Would you like to restore your last unsaved product configuration draft?</p>
                </div>
              </div>
              <div className="flex gap-2 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={() => {
                    const saved = localStorage.getItem('xtrem_admin_product_draft');
                    if (saved) {
                      setFormData(JSON.parse(saved));
                    }
                    setShowDraftBanner(false);
                  }}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm active:scale-95"
                >
                  Restore Draft
                </button>
                <button
                  type="button"
                  onClick={() => {
                    localStorage.removeItem('xtrem_admin_product_draft');
                    setShowDraftBanner(false);
                  }}
                  className="px-3 py-1.5 bg-white hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-lg text-xs font-semibold transition-all"
                >
                  Discard
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Product ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product ID (Code)</label>
              <input
                type="text"
                name="productId"
                required
                disabled={isEdit}
                placeholder="e.g. LIFE-001"
                className={`w-full rounded-lg border px-3 py-2 transition-all ${
                  validationErrors.productId 
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300'
                } ${isEdit ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`}
                value={formData.productId}
                onChange={handleChange}
              />
              {validationErrors.productId && (
                <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.productId}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                name="category"
                className={`w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white ${
                  validationErrors.category ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                value={formData.category}
                onChange={handleChange}
              >
                <option value="Insurance">Insurance</option>
                <option value="Pension">Pension</option>
                <option value="Banking">Banking</option>
              </select>
              {validationErrors.category && (
                <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.category}</p>
              )}
            </div>

            {/* Status & Discontinued Toggle */}
            <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center gap-3 flex-1">
                <div
                  className={`w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out cursor-pointer ${formData.isActive ? 'bg-green-600' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                >
                  <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${formData.isActive ? 'translate-x-4' : ''}`}></div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-800 cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}>
                    Product is Active
                  </label>
                  <p className="text-[10px] text-gray-500 italic">Inactive products are hidden from everyone.</p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-1 border-t sm:border-t-0 sm:border-l border-gray-200 pt-3 sm:pt-0 sm:pl-4">
                <div
                  className={`w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out cursor-pointer ${formData.isDiscontinued ? 'bg-orange-600' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, isDiscontinued: !prev.isDiscontinued }))}
                >
                  <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${formData.isDiscontinued ? 'translate-x-4' : ''}`}></div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-800 cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, isDiscontinued: !prev.isDiscontinued }))}>
                    Product is Discontinued
                  </label>
                  <p className="text-[10px] text-orange-600 italic">Hidden from catalog, visible to existing owners.</p>
                </div>
              </div>
            </div>

            {/* Company Assignment (Super Admin Only) */}
            {isSuperAdmin && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-teal-700 mb-1 font-bold italic">Assign to Company (Super Admin Only)</label>
                <select
                  name="companyId"
                  className="w-full rounded-lg border-teal-300 border px-3 py-2 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition bg-teal-50"
                  value={formData.companyId || ''}
                  onChange={handleChange}
                >
                  <option value="">Global (All Companies)</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-teal-600 opacity-75">Company Admins can only see products assigned to their company or Global products.</p>
              </div>
            )}

            {/* Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
              <input
                type="text"
                name="name"
                required
                placeholder="e.g. Xtrem Family Protection Plan"
                className={`w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                  validationErrors.name ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                value={formData.name}
                onChange={handleChange}
              />
              {validationErrors.name && (
                <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.name}</p>
              )}
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <span className={`text-xs font-semibold ${
                  (formData.description || '').length > 250 ? 'text-red-500' : 'text-gray-400'
                }`}>
                  {(formData.description || '').length}/250
                </span>
              </div>
              <textarea
                name="description"
                rows={3}
                placeholder="Briefly describe the product features..."
                className={`w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none ${
                  validationErrors.description || (formData.description || '').length > 250
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300'
                }`}
                value={formData.description}
                onChange={handleChange}
              />
              {(validationErrors.description || (formData.description || '').length > 250) && (
                <p className="mt-1 text-xs text-red-500 font-medium">
                  {validationErrors.description || "Description cannot exceed 250 characters."}
                </p>
              )}
            </div>

            {/* Image Upload */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Image</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors bg-gray-50/50">
                <div className="space-y-1 text-center">
                  {formData.imageUrl ? (
                    <div className="relative inline-block group">
                      <img src={formData.imageUrl} alt="Preview" className="mx-auto h-48 object-contain rounded-md shadow-sm" />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-md transform transition-transform group-hover:scale-110"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none underline decoration-2 decoration-blue-200 hover:decoration-blue-500">
                          <span>Upload a file</span>
                          <input id="file-upload" name="file-upload" type="file" accept="image/*" ref={fileInputRef} className="sr-only" onChange={handleImageUpload} />
                        </label>
                        <p className="pl-1 text-gray-500">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-400 font-medium tracking-tight">PNG, JPG, GIF up to 2MB</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Premiums */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Premium (MK)</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-400 text-sm">MK</span>
                <input
                  type="number"
                  name="monthlyPremium"
                  min="0"
                  step="0.01"
                  className={`w-full rounded-lg border pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                    validationErrors.monthlyPremium ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  value={formData.monthlyPremium || ''}
                  onChange={handleChange}
                />
              </div>
              {validationErrors.monthlyPremium && (
                <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.monthlyPremium}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Premium (MK)</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-400 text-sm">MK</span>
                <input
                  type="number"
                  name="yearlyPremium"
                  min="0"
                  step="0.01"
                  className={`w-full rounded-lg border pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                    validationErrors.yearlyPremium ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  value={formData.yearlyPremium || ''}
                  onChange={handleChange}
                />
              </div>
              {validationErrors.yearlyPremium && (
                <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.yearlyPremium}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Standard Payout (MK)</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-400 text-sm">MK</span>
                <input
                  type="number"
                  name="payout"
                  min="0"
                  step="0.01"
                  className={`w-full rounded-lg border pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                    validationErrors.payout ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  value={formData.payout || ''}
                  onChange={handleChange}
                />
              </div>
              {validationErrors.payout && (
                <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.payout}</p>
              )}
            </div>

            {/* Once-off Premium */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Standard Once-off Premium (MK)</label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-400 text-sm">MK</span>
                <input
                  type="number"
                  name="onceOffPremium"
                  min="0"
                  step="0.01"
                  className={`w-full rounded-lg border pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition ${
                    validationErrors.onceOffPremium ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                  value={formData.onceOffPremium || ''}
                  onChange={handleChange}
                />
              </div>
              {validationErrors.onceOffPremium && (
                <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.onceOffPremium}</p>
              )}
            </div>

            {!formData.hasTiers && validationErrors.premiums && (
              <div className="md:col-span-2 text-xs text-red-500 font-medium bg-red-50 border-l-4 border-red-500 p-3 rounded-r-lg">
                {validationErrors.premiums}
              </div>
            )}

            {/* Premium Due Date & Expiry Configuration */}
            <div className="md:col-span-2 border-t pt-4 mt-2">
              <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Premium Due Date & Expiry Configuration
              </h4>
              <p className="text-xs text-gray-500 mb-4">Define when premiums are due and how long policies last</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Premium Due Day of Month</label>
                  <input
                    type="number"
                    name="premiumDueDayOfMonth"
                    min="1"
                    max="31"
                    placeholder="e.g. 15"
                    className={`w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition ${
                      validationErrors.premiumDueDayOfMonth ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    value={formData.premiumDueDayOfMonth || ''}
                    onChange={handleChange}
                  />
                  {validationErrors.premiumDueDayOfMonth ? (
                    <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.premiumDueDayOfMonth}</p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">Day of month (1-31)</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy Duration (Months)</label>
                  <input
                    type="number"
                    name="policyDurationMonths"
                    min="1"
                    max="120"
                    placeholder="e.g. 12"
                    className={`w-full rounded-lg border px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition ${
                      validationErrors.policyDurationMonths ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    value={formData.policyDurationMonths || ''}
                    onChange={handleChange}
                  />
                  {validationErrors.policyDurationMonths ? (
                    <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.policyDurationMonths}</p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">How long the policy lasts</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Premium Frequency</label>
                  <select
                    name="premiumFrequency"
                    className="w-full rounded-lg border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition bg-white"
                    value={formData.premiumFrequency || 'Monthly'}
                    onChange={handleChange}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Yearly">Yearly</option>
                    <option value="Once-off">Once-off</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">How often premiums are paid</p>
                </div>
              </div>
            </div>

            {/* Beneficiary Configuration */}
            <div className="md:col-span-2 border-t pt-4 mt-2">
              <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Beneficiary Configuration
              </h4>

              <div className="bg-pink-50/50 p-4 rounded-xl border border-pink-100 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out cursor-pointer ${formData.allowsBeneficiaries ? 'bg-pink-600' : ''}`} onClick={() => setFormData(prev => ({ ...prev, allowsBeneficiaries: !prev.allowsBeneficiaries }))}>
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${formData.allowsBeneficiaries ? 'translate-x-4' : ''}`}></div>
                  </div>
                  <label className="text-sm font-semibold text-pink-900 cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, allowsBeneficiaries: !prev.allowsBeneficiaries }))}>
                    Allow Customers to Add Beneficiaries during Purchase
                  </label>
                </div>

                {formData.allowsBeneficiaries && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 animate-fadeIn">
                    <div>
                      <label className="block text-xs font-bold text-pink-800 uppercase tracking-wider mb-1">Max Beneficiaries</label>
                      <input
                        type="number"
                        name="maxBeneficiaries"
                        min="1"
                        placeholder="e.g. 5"
                        className={`w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 outline-none transition-all ${
                          validationErrors.maxBeneficiaries ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-pink-200'
                        }`}
                        value={formData.maxBeneficiaries}
                        onChange={handleChange}
                      />
                      {validationErrors.maxBeneficiaries && (
                        <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.maxBeneficiaries}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-pink-800 uppercase tracking-wider mb-1">Premium Increment per Beneficiary (MK)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-pink-400 text-sm font-bold">MK</span>
                        <input
                          type="number"
                          name="beneficiaryPremiumIncrement"
                          min="0"
                          step="0.01"
                          className={`w-full rounded-lg border pl-10 pr-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 outline-none transition-all ${
                            validationErrors.beneficiaryPremiumIncrement ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-pink-200'
                          }`}
                          value={formData.beneficiaryPremiumIncrement || ''}
                          onChange={handleChange}
                        />
                      </div>
                      {validationErrors.beneficiaryPremiumIncrement && (
                        <p className="mt-1 text-xs text-red-500 font-medium">{validationErrors.beneficiaryPremiumIncrement}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tiers Toggle */}
            <div className="md:col-span-2 flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, hasTiers: !prev.hasTiers }))}>
              <div className={`w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out ${formData.hasTiers ? 'bg-blue-600' : ''}`}>
                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${formData.hasTiers ? 'translate-x-4' : ''}`}></div>
              </div>
              <label className="ml-3 block text-sm font-semibold text-blue-800 cursor-pointer">
                Enable Product Tiers (e.g. Gold, Silver, Bronze)
              </label>
            </div>

            {/* Tiers Management */}
            {formData.hasTiers && (
              <div className="md:col-span-2 space-y-4 border-l-4 border-blue-200 pl-4 py-2">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-bold text-gray-800">Product Tiers</h4>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      tiers: [...(prev.tiers || []), { name: '', monthlyPremium: 0, yearlyPremium: 0, payout: 0, description: '', onceOffPremium: 0 }]
                    }))}
                    className="text-blue-600 hover:text-blue-800 text-xs font-bold"
                  >
                    + Add New Tier
                  </button>
                </div>

                {(formData.tiers || []).map((tier, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg space-y-3 relative group">
                    <button
                      type="button"
                      onClick={() => {
                        const newTiers = [...(formData.tiers || [])];
                        newTiers.splice(idx, 1);
                        setFormData(prev => ({ ...prev, tiers: newTiers }));
                      }}
                      className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Tier Name</label>
                        <input
                          type="text"
                          placeholder="e.g. Gold"
                          className="w-full rounded-md border-gray-300 border px-3 py-1.5 text-sm"
                          value={tier.name}
                          onChange={(e) => {
                            const newTiers = [...(formData.tiers || [])];
                            newTiers[idx].name = e.target.value;
                            setFormData(prev => ({ ...prev, tiers: newTiers }));
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Policy Duration (Months)</label>
                        <input
                          type="number"
                          placeholder="e.g. 12"
                          className="w-full rounded-md border-gray-300 border px-3 py-1.5 text-sm"
                          value={tier.policyDurationMonths || ''}
                          onChange={(e) => {
                            const newTiers = [...(formData.tiers || [])];
                            newTiers[idx].policyDurationMonths = e.target.value ? parseInt(e.target.value) : undefined;
                            setFormData(prev => ({ ...prev, tiers: newTiers }));
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Payout (MK)</label>
                        <input
                          type="number"
                          placeholder="Payout"
                          min="0"
                          step="0.01"
                          className="w-full rounded-md border-gray-300 border px-3 py-1.5 text-sm"
                          value={tier.payout || ''}
                          onChange={(e) => {
                            const newTiers = [...(formData.tiers || [])];
                            newTiers[idx].payout = e.target.value ? parseFloat(e.target.value) : 0;
                            setFormData(prev => ({ ...prev, tiers: newTiers }));
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Monthly Premium (MK)</label>
                        <input
                          type="number"
                          placeholder="Monthly"
                          min="0"
                          step="0.01"
                          className="w-full rounded-md border-gray-300 border px-3 py-1.5 text-sm"
                          value={tier.monthlyPremium || ''}
                          onChange={(e) => {
                            const newTiers = [...(formData.tiers || [])];
                            newTiers[idx].monthlyPremium = e.target.value ? parseFloat(e.target.value) : 0;
                            setFormData(prev => ({ ...prev, tiers: newTiers }));
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Yearly Premium (MK)</label>
                        <input
                          type="number"
                          placeholder="Yearly"
                          min="0"
                          step="0.01"
                          className="w-full rounded-md border-gray-300 border px-3 py-1.5 text-sm"
                          value={tier.yearlyPremium || ''}
                          onChange={(e) => {
                            const newTiers = [...(formData.tiers || [])];
                            newTiers[idx].yearlyPremium = e.target.value ? parseFloat(e.target.value) : 0;
                            setFormData(prev => ({ ...prev, tiers: newTiers }));
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Once-off Premium (MK)</label>
                        <input
                          type="number"
                          placeholder="Once-off"
                          min="0"
                          step="0.01"
                          className="w-full rounded-md border-gray-300 border px-3 py-1.5 text-sm"
                          value={tier.onceOffPremium || ''}
                          onChange={(e) => {
                            const newTiers = [...(formData.tiers || [])];
                            newTiers[idx].onceOffPremium = e.target.value ? parseFloat(e.target.value) : 0;
                            setFormData(prev => ({ ...prev, tiers: newTiers }));
                          }}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Description (Optional)</label>
                        <input
                          type="text"
                          placeholder="Short description of this tier..."
                          className="w-full rounded-md border-gray-300 border px-3 py-1.5 text-sm"
                          value={tier.description || ''}
                          onChange={(e) => {
                            const newTiers = [...(formData.tiers || [])];
                            newTiers[idx].description = e.target.value;
                            setFormData(prev => ({ ...prev, tiers: newTiers }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Eligibility Criteria Section */}
            <div className="md:col-span-2 border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Eligibility Criteria</label>
                  <p className="text-xs text-gray-500 italic">Define rules for when a customer can claim this product.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const current = JSON.parse(formData.eligibilityCriteriaJson || '[]');
                    current.push({ type: 'WaitingPeriod', value: 0, unit: 'Months', description: '' });
                    setFormData(prev => ({ ...prev, eligibilityCriteriaJson: JSON.stringify(current) }));
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs font-bold px-3 py-1 bg-blue-50 rounded-lg"
                >
                  + Add Rule
                </button>
              </div>

              <div className="space-y-4">
                {JSON.parse(formData.eligibilityCriteriaJson || '[]').map((rule: any, idx: number) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-100 relative group animate-in slide-in-from-top-2">
                    <button
                      type="button"
                      onClick={() => {
                        const current = JSON.parse(formData.eligibilityCriteriaJson || '[]');
                        current.splice(idx, 1);
                        setFormData(prev => ({ ...prev, eligibilityCriteriaJson: JSON.stringify(current) }));
                      }}
                      className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Rule Type</label>
                        <select
                          className="w-full rounded-lg border-gray-200 border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          value={rule.type}
                          onChange={(e) => {
                            const current = JSON.parse(formData.eligibilityCriteriaJson || '[]');
                            current[idx].type = e.target.value;
                            // Reset values based on type
                            if (e.target.value === 'WaitingPeriod') {
                              current[idx].unit = 'Months';
                            } else {
                              delete current[idx].unit;
                            }
                            setFormData(prev => ({ ...prev, eligibilityCriteriaJson: JSON.stringify(current) }));
                          }}
                        >
                          <option value="WaitingPeriod">Waiting Period (after purchase)</option>
                          <option value="MinimumAge">Minimum Age</option>
                          <option value="MinimumContribution">Minimum Contribution Amount</option>
                          <option value="Custom">Custom Text Rule</option>
                        </select>
                      </div>

                      {rule.type !== 'Custom' && (
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                            {rule.type === 'WaitingPeriod' ? 'Duration' : rule.type === 'MinimumAge' ? 'Age' : 'Amount (MK)'}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              className="flex-1 rounded-lg border-gray-200 border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                              value={rule.value || ''}
                              onChange={(e) => {
                                const current = JSON.parse(formData.eligibilityCriteriaJson || '[]');
                                current[idx].value = parseFloat(e.target.value) || 0;
                                setFormData(prev => ({ ...prev, eligibilityCriteriaJson: JSON.stringify(current) }));
                              }}
                            />
                            {rule.type === 'WaitingPeriod' && (
                              <select
                                className="w-24 rounded-lg border-gray-200 border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none hover:bg-white"
                                value={rule.unit}
                                onChange={(e) => {
                                  const current = JSON.parse(formData.eligibilityCriteriaJson || '[]');
                                  current[idx].unit = e.target.value;
                                  setFormData(prev => ({ ...prev, eligibilityCriteriaJson: JSON.stringify(current) }));
                                }}
                              >
                                <option value="Months">Months</option>
                                <option value="Years">Years</option>
                              </select>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Display Description</label>
                        <input
                          type="text"
                          className="w-full rounded-lg border-gray-200 border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                          value={rule.description}
                          placeholder={rule.type === 'WaitingPeriod' ? 'e.g. 6 Months waiting period' : rule.type === 'MinimumAge' ? 'e.g. Must be 18 or older' : 'e.g. Contributed at least MK 50,000'}
                          onChange={(e) => {
                            const current = JSON.parse(formData.eligibilityCriteriaJson || '[]');
                            current[idx].description = e.target.value;
                            setFormData(prev => ({ ...prev, eligibilityCriteriaJson: JSON.stringify(current) }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {(!formData.eligibilityCriteriaJson || JSON.parse(formData.eligibilityCriteriaJson).length === 0) && (
                  <div className="text-center py-6 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/30">
                    <p className="text-sm text-gray-400 font-medium">No eligibility rules defined.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Claim Requirements Section */}
            <div className="md:col-span-2 border-t pt-4">
              <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Claim Requirements Configuration
              </h4>
              <p className="text-xs text-gray-500 mb-4">Define what documents and information are needed when a customer submits a claim for this product.</p>

              <div className="space-y-6">
                {/* Required Documents */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-gray-700 uppercase">Required Documents for Claims</label>
                    <button
                      type="button"
                      onClick={() => {
                        const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                        config.requiredDocuments.push({ id: `doc_${Date.now()}`, name: '', description: '', isRequired: true });
                        setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                      }}
                      className="text-red-600 hover:text-red-800 text-[10px] font-bold px-2 py-1 bg-red-50 rounded"
                    >
                      + Add Document Requirement
                    </button>
                  </div>

                  <div className="space-y-3">
                    {(JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[]}').requiredDocuments || []).map((doc: any, idx: number) => (
                      <div key={doc.id || idx} className="p-3 bg-red-50/30 rounded-lg border border-red-100 relative group flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                            config.requiredDocuments.splice(idx, 1);
                            setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                          }}
                          className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="Document Name (e.g. Death Certificate)"
                            className="w-full rounded border-gray-200 border px-2 py-1 text-xs focus:ring-1 focus:ring-red-500 outline-none"
                            value={doc.name}
                            onChange={(e) => {
                              const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                              config.requiredDocuments[idx].name = e.target.value;
                              setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                            }}
                          />
                          <label className="flex items-center gap-2 text-[10px] text-gray-600">
                            <input
                              type="checkbox"
                              checked={doc.isRequired}
                              onChange={(e) => {
                                const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                                config.requiredDocuments[idx].isRequired = e.target.checked;
                                setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                              }}
                            />
                            Is Mandatory
                          </label>
                        </div>
                        <input
                          type="text"
                          placeholder="Short Description/Instruction"
                          className="w-full rounded border-gray-200 border px-2 py-1 text-[10px] focus:ring-1 focus:ring-red-500 outline-none"
                          value={doc.description}
                          onChange={(e) => {
                            const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                            config.requiredDocuments[idx].description = e.target.value;
                            setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                          }}
                        />
                      </div>
                    ))}
                    {(JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[]}').requiredDocuments?.length === 0) && (
                      <p className="text-[10px] text-gray-400 italic text-center py-2 bg-gray-50 rounded-lg">No document requirements defined. Only basic claim info will be collected.</p>
                    )}
                  </div>
                </div>

                {/* Custom Fields */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-bold text-gray-700 uppercase">Custom Claim Fields (Information to Collect)</label>
                    <button
                      type="button"
                      onClick={() => {
                        const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                        config.requiredFields.push({ id: `field_${Date.now()}`, label: '', type: 'text', isRequired: true });
                        setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                      }}
                      className="text-blue-600 hover:text-blue-800 text-[10px] font-bold px-2 py-1 bg-blue-50 rounded"
                    >
                      + Add Custom Field
                    </button>
                  </div>

                  <div className="space-y-3">
                    {(JSON.parse(formData.claimConfigurationJson || '{"requiredFields":[]}').requiredFields || []).map((field: any, idx: number) => (
                      <div key={field.id || idx} className="p-3 bg-blue-50/30 rounded-lg border border-blue-100 relative group flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                            config.requiredFields.splice(idx, 1);
                            setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                          }}
                          className="absolute top-2 right-2 text-blue-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="Field Label (e.g. Cause of Incident)"
                            className="w-full rounded border-gray-200 border px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 outline-none"
                            value={field.label}
                            onChange={(e) => {
                              const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                              config.requiredFields[idx].label = e.target.value;
                              setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                            }}
                          />
                          <select
                            className="w-full rounded border-gray-200 border px-2 py-1 text-[10px] focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                            value={field.type}
                            onChange={(e) => {
                              const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                              config.requiredFields[idx].type = e.target.value;
                              setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                            }}
                          >
                            <option value="text">Single Line Text</option>
                            <option value="textarea">Multi-line Text</option>
                            <option value="date">Date Picker</option>
                            <option value="number">Numeric Input</option>
                          </select>
                        </div>
                        <label className="flex items-center gap-2 text-[10px] text-gray-600">
                          <input
                            type="checkbox"
                            checked={field.isRequired}
                            onChange={(e) => {
                              const config = JSON.parse(formData.claimConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                              config.requiredFields[idx].isRequired = e.target.checked;
                              setFormData(prev => ({ ...prev, claimConfigurationJson: JSON.stringify(config) }));
                            }}
                          />
                          Is Mandatory
                        </label>
                      </div>
                    ))}
                    {(JSON.parse(formData.claimConfigurationJson || '{"requiredFields":[]}').requiredFields?.length === 0) && (
                      <p className="text-[10px] text-gray-400 italic text-center py-2 bg-gray-50 rounded-lg">No custom claim fields defined.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="md:col-span-2 border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-gray-700">Product Features</label>
                <button type="button" onClick={() => handleListUpdate('features', 'add')} className="text-blue-600 hover:text-blue-800 text-xs font-bold">+ Add Feature</button>
              </div>
              <div className="space-y-2">
                {JSON.parse(formData.features || '[]').map((feat: string, idx: number) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 rounded-lg border-gray-300 border px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                      value={feat}
                      placeholder="e.g. 24/7 Support"
                      onChange={(e) => handleListUpdate('features', 'update', idx, e.target.value)}
                    />
                    <button type="button" onClick={() => handleListUpdate('features', 'remove', idx)} className="text-red-400 hover:text-red-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits Section */}
            <div className="md:col-span-2 border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-gray-700">Benefits</label>
                <button type="button" onClick={() => handleListUpdate('benefits', 'add')} className="text-blue-600 hover:text-blue-800 text-xs font-bold">+ Add Benefit</button>
              </div>
              <div className="space-y-2">
                {JSON.parse(formData.benefits || '[]').map((ben: string, idx: number) => (
                  <div key={idx} className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 rounded-lg border-gray-300 border px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                      value={ben}
                      placeholder="e.g. Peace of mind"
                      onChange={(e) => handleListUpdate('benefits', 'update', idx, e.target.value)}
                    />
                    <button type="button" onClick={() => handleListUpdate('benefits', 'remove', idx)} className="text-red-400 hover:text-red-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQs Section */}
            <div className="md:col-span-2 border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-gray-700">FAQs</label>
                <button type="button" onClick={() => handleListUpdate('faqs', 'add')} className="text-blue-600 hover:text-blue-800 text-xs font-bold">+ Add FAQ</button>
              </div>
              <div className="space-y-4">
                {JSON.parse(formData.faqs || '[]').map((faq: any, idx: number) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg relative group">
                    <button type="button" onClick={() => handleListUpdate('faqs', 'remove', idx)} className="absolute top-2 right-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    <div className="space-y-2">
                      <input
                        type="text"
                        className="w-full rounded-lg border-gray-300 border px-3 py-1.5 text-sm font-medium focus:ring-1 focus:ring-blue-500 outline-none"
                        value={faq.question}
                        placeholder="Question"
                        onChange={(e) => {
                          const newList = JSON.parse(formData.faqs || '[]');
                          newList[idx].question = e.target.value;
                          setFormData(prev => ({ ...prev, faqs: JSON.stringify(newList) }));
                        }}
                      />
                      <textarea
                        className="w-full rounded-lg border-gray-300 border px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none resize-none"
                        rows={2}
                        value={faq.answer}
                        placeholder="Answer"
                        onChange={(e) => {
                          const newList = JSON.parse(formData.faqs || '[]');
                          newList[idx].answer = e.target.value;
                          setFormData(prev => ({ ...prev, faqs: JSON.stringify(newList) }));
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents Section */}
            <div className="md:col-span-2 border-t border-b py-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Related Documents</label>
                  <p className="text-xs text-gray-500">Upload PDF brochures, terms, or flyers</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const currentDocs = JSON.parse(formData.documents || '[]');
                    const newList = [...currentDocs, { name: '', content: '', type: '' }];
                    setFormData(prev => ({ ...prev, documents: JSON.stringify(newList) }));
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs font-bold px-3 py-1 bg-blue-50 rounded-lg"
                >
                  + Add Document
                </button>
              </div>

              <div className="space-y-4">
                {JSON.parse(formData.documents || '[]').map((doc: any, idx: number) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-xl relative group border border-gray-100">
                    <button
                      type="button"
                      onClick={() => {
                        const currentDocs = JSON.parse(formData.documents || '[]');
                        currentDocs.splice(idx, 1);
                        setFormData(prev => ({ ...prev, documents: JSON.stringify(currentDocs) }));
                      }}
                      className="absolute -top-2 -right-2 bg-white text-red-400 hover:text-red-600 rounded-full p-1 shadow-sm border border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Document Label</label>
                        <input
                          type="text"
                          className="w-full rounded-lg border-gray-200 border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                          value={doc.name}
                          placeholder="e.g. Policy Wording (PDF)"
                          onChange={(e) => {
                            const currentDocs = JSON.parse(formData.documents || '[]');
                            currentDocs[idx].name = e.target.value;
                            setFormData(prev => ({ ...prev, documents: JSON.stringify(currentDocs) }));
                          }}
                        />
                      </div>

                      <div className="flex flex-col justify-end">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Upload File</label>
                        <div className="flex items-center gap-2">
                          <label className="flex-1 cursor-pointer bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors flex items-center gap-2">
                            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <span className="truncate">{doc.type ? `File Attached (${doc.type.split('/')[1].toUpperCase()})` : "Select File..."}</span>
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.doc,.docx,image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  if (file.size > 5 * 1024 * 1024) {
                                    toast.error("File is too large (max 5MB)");
                                    return;
                                  }
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    const currentDocs = JSON.parse(formData.documents || '[]');
                                    currentDocs[idx].content = reader.result as string;
                                    currentDocs[idx].type = file.type;
                                    if (!currentDocs[idx].name) currentDocs[idx].name = file.name.split('.')[0];
                                    setFormData(prev => ({ ...prev, documents: JSON.stringify(currentDocs) }));
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                          </label>
                          {doc.content && (
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {(!formData.documents || JSON.parse(formData.documents).length === 0) && (
                  <div className="text-center py-6 border-2 border-dashed border-gray-100 rounded-xl">
                    <p className="text-sm text-gray-400 font-medium">No documents added yet.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Dynamic Questions Section */}
            <div className="md:col-span-2 border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-semibold text-gray-700">Dynamic Policy Questions</label>
                <button
                  type="button"
                  onClick={() => {
                    const current = JSON.parse(formData.questionsJson || '[]');
                    current.push({ id: `q_${Date.now()}`, name: '', label: '', type: 'text', required: true, options: [], dependsOn: '', dependsOnValue: '' });
                    setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                  }}
                  className="text-blue-600 hover:text-blue-800 text-xs font-bold"
                >
                  + Add Question
                </button>
              </div>
              <p className="text-xs text-gray-400 mb-4">Add form fields that customers must fill during purchase.</p>

              <div className="space-y-4">
                {JSON.parse(formData.questionsJson || '[]').map((q: any, idx: number) => (
                  <div key={q.id || idx} className="p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-3">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            className="w-full rounded-lg border-gray-300 border px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            value={q.label}
                            placeholder="Question Label (e.g. Vehicle Reg)"
                            onChange={(e) => {
                              const current = JSON.parse(formData.questionsJson || '[]');
                              current[idx].label = e.target.value;
                              setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                            }}
                          />
                          <input
                            type="text"
                            className="w-full rounded-lg border-gray-300 border px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none bg-blue-50/30"
                            value={q.name}
                            placeholder="System Name (e.g. reg_no)"
                            onChange={(e) => {
                              const current = JSON.parse(formData.questionsJson || '[]');
                              current[idx].name = e.target.value.toLowerCase().replace(/\s+/g, '_');
                              setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                            }}
                          />
                        </div>
                        <div className="flex gap-4">
                          <select
                            className="rounded-lg border-gray-300 border px-2 py-1 text-xs focus:ring-1 focus:ring-blue-500 outline-none"
                            value={q.type}
                            onChange={(e) => {
                              const current = JSON.parse(formData.questionsJson || '[]');
                              current[idx].type = e.target.value;
                              setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                            }}
                          >
                            <option value="text">Text Input</option>
                            <option value="radio">Radio Buttons</option>
                            <option value="checkbox">Single Checkbox</option>
                            <option value="checkbox-group">Multiple Checkboxes</option>
                            <option value="select">Dropdown</option>
                          </select>
                          <label className="flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={q.required}
                              onChange={(e) => {
                                const current = JSON.parse(formData.questionsJson || '[]');
                                current[idx].required = e.target.checked;
                                setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                              }}
                            />
                            Required
                          </label>

                          {/* Conditional Logic */}
                          <div className="flex items-center gap-2 border-l pl-4 ml-auto">
                            <span className="text-[10px] font-bold text-gray-400 font-mono">DEPENDENCY:</span>
                            <select
                              className="rounded-lg border-gray-200 border px-2 py-1 text-[10px] focus:ring-1 focus:ring-blue-500 outline-none bg-white max-w-[120px]"
                              value={q.dependsOn || ''}
                              onChange={(e) => {
                                const current = JSON.parse(formData.questionsJson || '[]');
                                current[idx].dependsOn = e.target.value;
                                setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                              }}
                            >
                              <option value="">Always Show</option>
                              {JSON.parse(formData.questionsJson || '[]')
                                .slice(0, idx)
                                .filter((prevQ: any) => prevQ.type === 'radio' || prevQ.type === 'select' || prevQ.type === 'checkbox')
                                .map((prevQ: any) => (
                                  <option key={prevQ.id} value={prevQ.id}>{prevQ.label || prevQ.name || 'Untitled'}</option>
                                ))}
                            </select>
                            {q.dependsOn && (
                              <input
                                type="text"
                                className="rounded-lg border-gray-200 border px-2 py-1 text-[10px] focus:ring-1 focus:ring-blue-500 outline-none w-20"
                                placeholder="Answer..."
                                value={q.dependsOnValue || ''}
                                onChange={(e) => {
                                  const current = JSON.parse(formData.questionsJson || '[]');
                                  current[idx].dependsOnValue = e.target.value;
                                  setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                                }}
                              />
                            )}
                          </div>
                        </div>

                        {(q.type === 'radio' || q.type === 'select' || q.type === 'checkbox-group') && (
                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-gray-500 uppercase">Options (Comma separated)</label>
                            <input
                              type="text"
                              className="w-full rounded-lg border-gray-300 border px-3 py-1.5 text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                              value={q.options?.join(',') || ''}
                              placeholder="e.g. Option 1,Option 2"
                              onChange={(e) => {
                                const current = JSON.parse(formData.questionsJson || '[]');
                                current[idx].options = e.target.value.split(',');
                                setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                              }}
                              onBlur={() => {
                                const current = JSON.parse(formData.questionsJson || '[]');
                                if (current[idx].options) {
                                  current[idx].options = current[idx].options
                                    .map((s: string) => s.trim())
                                    .filter((s: string) => s);
                                  setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const current = JSON.parse(formData.questionsJson || '[]');
                          current.splice(idx, 1);
                          setFormData(prev => ({ ...prev, questionsJson: JSON.stringify(current) }));
                        }}
                        className="text-red-400 hover:text-red-600"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                ))}

                {(!formData.questionsJson || JSON.parse(formData.questionsJson).length === 0) && (
                  <div className="text-center py-4 border-2 border-dashed border-gray-100 rounded-xl">
                    <p className="text-sm text-gray-400 font-medium">No questions added yet.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Declaration Section */}
            <div className="md:col-span-2 border-t pt-4">
              <label className="text-sm font-semibold text-gray-700 mb-2 block">Policy Declaration</label>
              <p className="text-xs text-gray-400 mb-2">Text the customer must accept before purchasing.</p>
              <textarea
                name="declarationText"
                rows={4}
                placeholder="I hereby declare that the information provided is true..."
                className="w-full rounded-lg border-gray-300 border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
                value={formData.declarationText}
                onChange={handleChange}
              />
            </div>

            {/* Approval Configuration */}
            <div className="md:col-span-2 space-y-4 bg-purple-50 p-4 rounded-xl border border-purple-100">
              <h4 className="text-sm font-bold text-purple-900 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Approval & Onboarding Configuration
              </h4>

              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200 cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, requiresApproval: !prev.requiresApproval }))}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out ${formData.requiresApproval ? 'bg-purple-600' : ''}`}>
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${formData.requiresApproval ? 'translate-x-4' : ''}`}></div>
                  </div>
                  <label className="text-sm font-semibold text-purple-800 cursor-pointer">
                    Requires Xtrem Approval (Policy Applications Flow)
                  </label>
                </div>
              </div>

              {formData.requiresApproval && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                  <p className="text-[10px] text-purple-600 italic">Configure the required documents and fields that the customer MUST submit during the purchase flow.</p>

                  <div className="space-y-2 border-t border-purple-100 pt-3">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-purple-800 uppercase">Documents Needed for Application</label>
                      <button
                        type="button"
                        onClick={() => {
                          const config = JSON.parse(formData.onboardingConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                          if (!config.requiredDocuments) config.requiredDocuments = [];
                          config.requiredDocuments.push({ id: `doc_${Date.now()}`, name: '', description: '', isRequired: true });
                          setFormData(prev => ({ ...prev, onboardingConfigurationJson: JSON.stringify(config) }));
                        }}
                        className="text-purple-700 hover:text-purple-900 text-[10px] font-bold px-2 py-1 bg-purple-100 rounded"
                      >
                        + Add Policy Requirement
                      </button>
                    </div>

                    <div className="space-y-3">
                      {(JSON.parse(formData.onboardingConfigurationJson || '{"requiredDocuments":[]}').requiredDocuments || []).map((doc: any, idx: number) => (
                        <div key={doc.id || idx} className="p-3 bg-white/60 rounded-lg border border-purple-200 relative group flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const config = JSON.parse(formData.onboardingConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                              config.requiredDocuments.splice(idx, 1);
                              setFormData(prev => ({ ...prev, onboardingConfigurationJson: JSON.stringify(config) }));
                            }}
                            className="absolute top-2 right-2 text-purple-400 hover:text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="Document Name (e.g. Vehicle Bluebook)"
                              className="w-full rounded border-purple-200 border px-2 py-1 text-xs focus:ring-1 focus:ring-purple-500 outline-none bg-white"
                              value={doc.name}
                              onChange={(e) => {
                                const config = JSON.parse(formData.onboardingConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                                config.requiredDocuments[idx].name = e.target.value;
                                setFormData(prev => ({ ...prev, onboardingConfigurationJson: JSON.stringify(config) }));
                              }}
                            />
                            <label className="flex items-center gap-2 text-[10px] text-gray-600">
                              <input
                                type="checkbox"
                                checked={doc.isRequired}
                                onChange={(e) => {
                                  const config = JSON.parse(formData.onboardingConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                                  config.requiredDocuments[idx].isRequired = e.target.checked;
                                  setFormData(prev => ({ ...prev, onboardingConfigurationJson: JSON.stringify(config) }));
                                }}
                              />
                              Is Mandatory
                            </label>
                          </div>
                          <input
                            type="text"
                            placeholder="Help text for customer"
                            className="w-full rounded border-purple-200 border px-2 py-1 text-[10px] focus:ring-1 focus:ring-purple-500 outline-none bg-white"
                            value={doc.description}
                            onChange={(e) => {
                              const config = JSON.parse(formData.onboardingConfigurationJson || '{"requiredDocuments":[],"requiredFields":[]}');
                              config.requiredDocuments[idx].description = e.target.value;
                              setFormData(prev => ({ ...prev, onboardingConfigurationJson: JSON.stringify(config) }));
                            }}
                          />
                        </div>
                      ))}
                      {(JSON.parse(formData.onboardingConfigurationJson || '{"requiredDocuments":[]}').requiredDocuments?.length === 0) && (
                        <p className="text-[10px] text-purple-400 italic text-center py-2 bg-white/50 rounded-lg">No required documents defined. Customer only pays.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Recommended Toggle */}
            <div className="md:col-span-2 flex items-center p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, isRecommended: !prev.isRecommended }))}>
              <div className={`w-10 h-6 flex items-center bg-gray-300 rounded-full p-1 duration-300 ease-in-out ${formData.isRecommended ? 'bg-yellow-500' : ''}`}>
                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${formData.isRecommended ? 'translate-x-4' : ''}`}></div>
              </div>
              <label className="ml-3 block text-sm font-semibold text-yellow-800 cursor-pointer">
                Mark as "Recommended" (Featured on Dashboard)
              </label>
            </div>

          </div>

          {/* Actions */}
          <div className="flex justify-between items-center gap-4">
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="px-6 py-2.5 bg-white border-2 border-red-100 text-red-600 rounded-xl font-bold text-sm hover:bg-red-50 hover:border-red-200 transition-all disabled:opacity-50"
              >
                Delete Product
              </button>
            )}
            <div className="flex gap-4 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-gray-500 font-semibold text-sm hover:text-gray-700 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2"
              >
                {loading && (
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isEdit ? 'Save Changes' : 'Create Product'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}